import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const routeMetadata = {
  '/': {
    title: 'BlueWave Ship Management | Afloat Ship Repair, Marine Engineering & Crew Supply Sri Lanka',
    description:
      'Premier marine engineering and ship management in Sri Lanka. 24/7 afloat ship repairs, certified drydocking, crew manning, and tug services across Colombo Port, Galle, Hambantota & Indian Ocean.',
  },
  '/about': {
    title: 'About BlueWave | Marine Engineering & Fleet Management Excellence Sri Lanka',
    description:
      'Learn about BlueWave Ship Management (CIAP Pvt Ltd), our maritime legacy, certified technical superintendents, and commitment to zero harm across global waters.',
  },
  '/services': {
    title: 'Marine & Offshore Services | BlueWave Ship Management Sri Lanka',
    description:
      'Comprehensive marine solutions: Afloat ship repairs, technical ship management, drydocking, crew manning, tug & barge, bunker supply, and spare parts procurement.',
  },
  '/services/ship-management': {
    title: 'Technical & Commercial Ship Management | BlueWave Maritime',
    description:
      'End-to-end vessel operations, ISM/ISPS safety compliance, planned maintenance, and commercial chartering management for global shipowners.',
  },
  '/services/voyage': {
    title: 'Afloat Ship Repairs & Voyage Engineering Sri Lanka | BlueWave',
    description:
      '24/7 emergency afloat repairs, main engine overhaul, steel renewal, piping, and hydraulic systems repair at Colombo Port and Galle anchorages.',
  },
  '/services/drydock': {
    title: 'Ship Drydock Services & Shipyard Project Management Sri Lanka',
    description:
      'Specialized shipyard superintendence, drydock planning, hull blasting, coating, and classification survey preparation at premier regional drydocks.',
  },
  '/services/crew': {
    title: 'Crew Manning & Seafarer Recruitment Sri Lanka | MLC 2006 Certified',
    description:
      'Provision of certified deck officers, marine engineers, and ratings. Thoroughly vetted under STCW and Maritime Labour Convention (MLC 2006) standards.',
  },
  '/services/marine': {
    title: 'Marine Technical Diagnostics & Engineering Services Sri Lanka',
    description:
      'Precision marine mechanical engineering, electrical troubleshooting, vibration analysis, and turbocharger repair across South Asian ports.',
  },
  '/services/tug-barge-operations': {
    title: 'Tug & Barge Operations | Harbor Towage & Coastal Transportation',
    description:
      'Modern tugboat fleet for harbor assistance, coastal barge operations, heavy marine transport, and ocean salvage support in Sri Lanka.',
  },
  '/services/bunker': {
    title: 'Bunker Fuel & Fresh Water Supply Sri Lanka | Colombo & Galle OPL',
    description:
      'Reliable, on-schedule marine bunker fuels (MGO, VLSFO), lubricants, and high-quality potable fresh water supply at ports and off-port limits (OPL).',
  },
  '/services/spare': {
    title: 'Ship Spare Parts Procurement & Marine Logistics Sri Lanka',
    description:
      'Rapid marine spare parts sourcing, OEM engine components, bonded transit clearance, and express delivery to vessels in Sri Lankan waters.',
  },
  '/services/maritime-security': {
    title: 'Maritime Security & Risk Assessment | Indian Ocean High Risk Area',
    description:
      'Comprehensive ISPS code compliance, vessel hardening, anti-piracy risk assessments, and secure crew transit services in the Indian Ocean.',
  },
  '/services/ship-design-naval-architecture': {
    title: 'Ship Design & Naval Architecture Services Sri Lanka | BlueWave',
    description:
      'Vessel modification, stability calculation, 3D hull modeling, retrofit engineering, and classification society approval drawings.',
  },
  '/projects': {
    title: 'Marine Engineering Projects & Drydock Case Studies | BlueWave',
    description:
      'Explore successful afloat ship repairs, propeller repairs, engine overhauls, and drydock conversions delivered by BlueWave engineering teams.',
  },
  '/news': {
    title: 'Maritime News & Marine Industry Insights | BlueWave Sri Lanka',
    description:
      'Latest updates on Sri Lankan maritime corridors, Colombo Port developments, environmental regulations, and BlueWave company announcements.',
  },
  '/contact': {
    title: 'Contact BlueWave Ship Management | 24/7 Marine Emergency Hotline',
    description:
      'Get in touch with our 24/7 marine operations desk. Telephone: +94 719 745 745 | Email: info@bluewavesshipmanagement.com | Colombo, Sri Lanka.',
  },
  '/career': {
    title: 'Maritime Careers & Shore-Based Vacancies | BlueWave Ship Management',
    description:
      'Join our growing maritime team. Explore officer vacancies, rating positions, and technical superintendency opportunities in Sri Lanka.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | BlueWave Ship Management',
    description: 'Privacy policy and data protection commitments of BlueWave Ship Management (CIAP Pvt Ltd).',
  },
  '/terms-and-conditions': {
    title: 'Terms & Conditions | BlueWave Ship Management',
    description: 'Operating terms and maritime service conditions of BlueWave Ship Management (CIAP Pvt Ltd).',
  },
};

const SEO = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const meta = routeMetadata[path] || routeMetadata['/'];

    // Update document title
    document.title = meta.title;

    // Update Meta Description
    let descTag = document.querySelector('meta[name="description"]');
    if (descTag) {
      descTag.setAttribute('content', meta.description);
    }

    // Update Open Graph tags
    let ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) {
      ogTitleTag.setAttribute('content', meta.title);
    }
    let ogDescTag = document.querySelector('meta[property="og:description"]');
    if (ogDescTag) {
      ogDescTag.setAttribute('content', meta.description);
    }
    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (ogUrlTag) {
      ogUrlTag.setAttribute('content', `https://bluewavesshipmanagement.com${location.pathname}`);
    }

    // Update Twitter tags
    let twitterTitleTag = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitleTag) {
      twitterTitleTag.setAttribute('content', meta.title);
    }
    let twitterDescTag = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescTag) {
      twitterDescTag.setAttribute('content', meta.description);
    }

    // Update Canonical Link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', `https://bluewavesshipmanagement.com${location.pathname}`);
    }

    // Scroll to top smoothly on route change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return null;
};

export default SEO;
